import { useState, useEffect, useCallback } from "react";

// ─── GOOGLE FONTS ────────────────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,600;0,700;0,800;1,600&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,400&display=swap";
document.head.appendChild(fontLink);

// ─── GLOBAL STYLES ───────────────────────────────────────────────────────────
const style = document.createElement("style");
style.textContent = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #F2EDE4;
    --surface: #FDFAF5;
    --surface2: #F0EAE0;
    --border: #DDD6C8;
    --muted: #9E9082;
    --text: #2A2118;
    --accent: #3B2512;
    --accent2: #6B4226;
    --loved: #D4EDCC;
    --loved-border: #7DBE6A;
    --loved-text: #2A6B1A;
    --meh: #FFF0C2;
    --meh-border: #E6C840;
    --meh-text: #7A5C00;
    --hated: #FFD6D6;
    --hated-border: #E07070;
    --hated-text: #8B1A1A;
    --green: #7DBE6A;
    --yellow: #E6C840;
    --red: #E07070;
    --radius: 14px;
    --radius-sm: 8px;
    --shadow: 0 2px 12px rgba(42,33,24,0.08);
    --shadow-lg: 0 8px 32px rgba(42,33,24,0.14);
  }
  body { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--text); }
  input, textarea, button { font-family: inherit; }
  button { cursor: pointer; border: none; background: none; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 99px; }

  @keyframes slideUp {
    from { transform: translateY(24px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes checkPop {
    0%   { transform: scale(0.6); }
    60%  { transform: scale(1.15); }
    100% { transform: scale(1); }
  }
  @keyframes sheetUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
  .slide-up   { animation: slideUp 0.35s cubic-bezier(.22,1,.36,1) both; }
  .fade-in    { animation: fadeIn 0.25s ease both; }
  .sheet-up   { animation: sheetUp 0.32s cubic-bezier(.22,1,.36,1) both; }
`;
document.head.appendChild(style);

// ─── FULL SOCIALIZATION DATA ──────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: "handling", label: "Handling", emoji: "🖐️",
    items: [
      "Check ears", "Examine mouth & gums", "Open eyelids gently", "Squeeze paws",
      "Trim toenails", "Touch & squeeze nose", "Cradle on back in arms",
      "Hold in lap", "Hug puppy", "Pull collar gently (gotcha)",
      "Wipe body with towel", "Put on harness", "Put on head halter",
      "Brush fur", "Brush teeth", "Touch legs & stomach & tail",
      "Hold head or feet still", "Pinch skin gently"
    ]
  },
  {
    id: "people", label: "People", emoji: "👥",
    items: [
      "Men with beards", "Men with deep voices", "Tall men", "Women",
      "Elderly people", "Teenagers", "Children playing", "Toddlers walking & squealing",
      "Infants crawling", "People of many ethnicities", "Bald people",
      "People wearing hats or helmets", "People wearing hoodies",
      "People wearing backpacks", "People wearing sunglasses",
      "People with canes or walkers", "People in uniforms",
      "People in costumes", "People carrying packages",
      "People using wheelchairs or crutches", "People running toward puppy",
      "People dancing or gesturing", "People sweeping or mopping",
      "People laying on the ground", "Person falling or stumbling",
      "Crowd of people talking", "Delivery person at door"
    ]
  },
  {
    id: "sounds", label: "Sounds", emoji: "🔊",
    items: [
      "Doorbell", "Knocking on door", "Vacuum cleaner", "Hair dryer",
      "Thunder", "Fireworks", "Baby crying", "Kids playing loudly",
      "Smoke alarm", "Microwave beeping", "Washer & dryer", "TV on various channels",
      "Radio music", "Car horn", "Motorcycle engine", "Fire engine siren",
      "Police siren", "Helicopter or plane", "Lawnmower", "Leaf blower",
      "Construction jackhammer", "Hammering", "Pots and pans clanking",
      "Popcorn popping", "Alarm clock", "Dogs barking", "Cheering crowd",
      "Gunshot sound (from distance)", "Whistles"
    ]
  },
  {
    id: "surfaces", label: "Surfaces", emoji: "🌿",
    items: [
      "Concrete sidewalk", "Slippery tile or marble floor", "Hardwood floor",
      "Metal grate or manhole cover", "Wobbly surface or wobble board",
      "Stairs (up and down)", "Wet grass", "Mud", "Ice or snow",
      "Sand", "Rocky or uneven ground", "Artificial turf", "Rugs & carpet",
      "Metal vet scale", "Elevator floor", "Wooden dock or balcony"
    ]
  },
  {
    id: "animals", label: "Animals", emoji: "🐾",
    items: [
      "Friendly adult dog", "Dog that sets appropriate limits", "Puppy playdate",
      "Small breed dog", "Large breed dog", "Cat", "Horse or livestock",
      "Birds or waterfowl", "Squirrels or small wildlife"
    ]
  },
  {
    id: "vehicles", label: "Vehicles", emoji: "🚗",
    items: [
      "Cars passing by", "Buses", "Motorcycles", "Bicycles",
      "Skateboards", "Scooters or rollerblades", "Garbage truck",
      "Baby stroller", "Shopping cart", "Wheelchair", "Lawnmower moving",
      "Construction vehicle", "Car ride (inside)", "Car starting engine"
    ]
  },
  {
    id: "objects", label: "Objects", emoji: "📦",
    items: [
      "Umbrella opening and closing", "Balloons", "Broom or mop",
      "Plastic bags blowing in wind", "Large garbage bags", "Blankets being shaken",
      "Mirrors and reflections", "Ceiling fan on", "Sidewalk signs",
      "Metal objects", "Toys of different textures", "Pots and pans"
    ]
  },
  {
    id: "home", label: "At Home", emoji: "🏠",
    items: [
      "Hand in food bowl while eating", "Toy taken away gently",
      "Sleep in crate alone", "Be separated from owner (short periods)",
      "Bath time", "Hearing bath water run", "Towel dry after bath",
      "Present while vacuuming", "Present while sweeping or mopping",
      "Campfire or fireplace", "Ceiling fan", "Being alone in a room",
      "Doorbell with guests arriving", "Visit a friend's home"
    ]
  },
  {
    id: "gear", label: "Gear & Outings", emoji: "🧥",
    items: [
      "Wearing a collar", "Wearing a harness", "Wearing a muzzle",
      "Wearing a dog coat or jacket", "Wearing dog booties",
      "Walking on a leash", "Leash pressure", "Car ride",
      "Public transit (bus or subway)", "Pet store visit",
      "Vet clinic happy visit", "Groomer happy visit",
      "Outdoor market or event", "Dog-friendly restaurant patio"
    ]
  }
];

// tips for hated/meh items in suggestions
const TIPS = {
  sounds: "Try playing at very low volume first",
  vehicles: "Watch from a safe distance with treats",
  people: "Let the puppy approach at their own pace",
  animals: "Keep on leash, create distance first",
  surfaces: "Toss treats onto the surface to build curiosity",
  handling: "Start with brief touches, reward immediately",
  objects: "Let the puppy sniff it while it's still — no sudden movements",
  home: "Keep sessions short and positive",
  gear: "Introduce the item near the puppy before putting it on",
};

// ─── SIMULATED AUTH (swap these 3 functions for real Firebase) ───────────────
// 🔥 TO CONNECT FIREBASE: replace simulateLogin, simulateSignup, simulateLogout
// with Firebase auth calls: signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut
const simulateLogin = (email, password) =>
  new Promise((res, rej) => setTimeout(() => {
    const users = JSON.parse(localStorage.getItem("ww_users") || "{}");
    const u = users[email];
    if (u && u.password === password) res({ uid: email, name: u.name, email, puppyName: u.puppyName, puppyBreed: u.puppyBreed, puppyDob: u.puppyDob });
    else rej(new Error("Invalid email or password"));
  }, 600));

const simulateSignup = (name, email, password, puppyName, puppyBreed, puppyDob) =>
  new Promise((res, rej) => setTimeout(() => {
    const users = JSON.parse(localStorage.getItem("ww_users") || "{}");
    if (users[email]) { rej(new Error("An account with this email already exists")); return; }
    users[email] = { name, password, puppyName, puppyBreed, puppyDob };
    localStorage.setItem("ww_users", JSON.stringify(users));
    res({ uid: email, name, email, puppyName, puppyBreed, puppyDob });
  }, 700));

const simulateLogout = () => new Promise(res => setTimeout(res, 300));

// ─── STORAGE HELPERS ─────────────────────────────────────────────────────────
// 🔥 TO CONNECT FIREBASE: replace these with Firestore reads/writes
const loadProgress = (uid) => JSON.parse(localStorage.getItem(`ww_progress_${uid}`) || "{}");
const saveProgress = (uid, data) => localStorage.setItem(`ww_progress_${uid}`, JSON.stringify(data));
const loadSession  = () => JSON.parse(localStorage.getItem("ww_session") || "null");
const saveSession  = (user) => localStorage.setItem("ww_session", JSON.stringify(user));
const clearSession = () => localStorage.removeItem("ww_session");

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const reactionEmoji = { loved: "😍", meh: "😐", hated: "😨" };
const reactionLabel = { loved: "Loved it", meh: "Meh", hated: "Hated it" };
const reactionColor = {
  loved: { bg: "var(--loved)", border: "var(--loved-border)", text: "var(--loved-text)" },
  meh:   { bg: "var(--meh)",   border: "var(--meh-border)",   text: "var(--meh-text)"   },
  hated: { bg: "var(--hated)", border: "var(--hated-border)", text: "var(--hated-text)" },
};

const totalItems = CATEGORIES.reduce((s, c) => s + c.items.length, 0);

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

// Shared input
const Input = ({ label, type = "text", value, onChange, placeholder, autoComplete }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    <label style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)" }}>{label}</label>
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} autoComplete={autoComplete}
      style={{
        background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)",
        padding: "10px 13px", fontSize: 14, color: "var(--text)", outline: "none",
        transition: "border-color 0.2s",
      }}
      onFocus={e => e.target.style.borderColor = "var(--accent2)"}
      onBlur={e => e.target.style.borderColor = "var(--border)"}
    />
  </div>
);

// Primary button
const Btn = ({ children, onClick, disabled, variant = "primary", style: s }) => {
  const base = {
    borderRadius: "var(--radius)", padding: "13px 20px", fontSize: 14,
    fontFamily: "'Fraunces', serif", fontWeight: 700, letterSpacing: "0.04em",
    width: "100%", transition: "opacity 0.15s, transform 0.1s",
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
    ...s
  };
  const variants = {
    primary: { background: "var(--accent)", color: "white", border: "none" },
    secondary: { background: "transparent", color: "var(--accent)", border: "1.5px solid var(--border)" },
    ghost: { background: "var(--surface2)", color: "var(--text)", border: "none" },
  };
  return (
    <button onClick={disabled ? undefined : onClick} style={{ ...base, ...variants[variant] }}
      onMouseDown={e => { e.currentTarget.style.transform = "scale(0.98)"; }}
      onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}>
      {children}
    </button>
  );
};

// Bottom nav
const BottomNav = ({ tab, setTab }) => (
  <div style={{
    display: "flex", borderTop: "1px solid var(--border)",
    background: "var(--surface)", paddingBottom: "env(safe-area-inset-bottom, 8px)"
  }}>
    {[
      { id: "home", emoji: "🏠", label: "Home" },
      { id: "checklist", emoji: "✅", label: "Checklist" },
      { id: "progress", emoji: "📊", label: "Progress" },
    ].map(({ id, emoji, label }) => (
      <button key={id} onClick={() => setTab(id)} style={{
        flex: 1, padding: "10px 0 12px", display: "flex", flexDirection: "column",
        alignItems: "center", gap: 3, background: "none", border: "none", cursor: "pointer",
        color: tab === id ? "var(--accent)" : "var(--muted)",
        fontFamily: "'DM Sans', sans-serif",
      }}>
        <span style={{ fontSize: 20, lineHeight: 1 }}>{emoji}</span>
        <span style={{ fontSize: 10, fontWeight: tab === id ? 600 : 400, letterSpacing: "0.05em", textTransform: "uppercase" }}>{label}</span>
      </button>
    ))}
  </div>
);

// Progress bar
const ProgressBar = ({ value, color = "var(--accent)", height = 7 }) => (
  <div style={{ background: "var(--surface2)", borderRadius: 99, height, overflow: "hidden" }}>
    <div style={{ width: `${Math.min(100, value)}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.6s cubic-bezier(.22,1,.36,1)" }} />
  </div>
);

// Stat card
const StatCard = ({ num, label, color }) => (
  <div style={{ background: "var(--surface)", borderRadius: "var(--radius)", padding: "12px 8px", textAlign: "center", boxShadow: "var(--shadow)", border: "1px solid var(--border)" }}>
    <div style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{num}</div>
    <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 3 }}>{label}</div>
  </div>
);

// ─── SCREEN: AUTH ─────────────────────────────────────────────────────────────
const AuthScreen = ({ onAuth }) => {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [puppyName, setPuppyName] = useState("");
  const [puppyBreed, setPuppyBreed] = useState("");
  const [puppyDob, setPuppyDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handle = async () => {
    setError(""); setLoading(true);
    try {
      let user;
      if (mode === "login") {
        user = await simulateLogin(email, password);
      } else {
        if (!name.trim() || !email.trim() || !password.trim() || !puppyName.trim()) {
          throw new Error("Please fill in all required fields");
        }
        user = await simulateSignup(name.trim(), email.trim(), password, puppyName.trim(), puppyBreed.trim(), puppyDob);
      }
      saveSession(user);
      onAuth(user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px", background: "var(--bg)" }}>
      <div className="slide-up" style={{ width: "100%", maxWidth: 380, display: "flex", flexDirection: "column", gap: 0 }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 52, marginBottom: 6 }}>🐾</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 800, color: "var(--accent)" }}>WoofWide</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>Try new things. Build confidence. Have fun.</div>
        </div>

        {/* Card */}
        <div style={{ background: "var(--surface)", borderRadius: 20, padding: 24, boxShadow: "var(--shadow-lg)", border: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Mode tabs */}
          <div style={{ display: "flex", background: "var(--surface2)", borderRadius: "var(--radius-sm)", padding: 3 }}>
            {["login", "signup"].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(""); }} style={{
                flex: 1, padding: "7px 0", borderRadius: 7, fontSize: 13, fontWeight: 600,
                background: mode === m ? "var(--surface)" : "transparent",
                color: mode === m ? "var(--accent)" : "var(--muted)",
                boxShadow: mode === m ? "var(--shadow)" : "none",
                transition: "all 0.2s", border: "none", cursor: "pointer"
              }}>
                {m === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>

          {mode === "signup" && <Input label="Your name *" value={name} onChange={setName} placeholder="Maria" autoComplete="name" />}
          <Input label="Email *" type="email" value={email} onChange={setEmail} placeholder="you@email.com" autoComplete="email" />
          <Input label="Password *" type="password" value={password} onChange={setPassword} placeholder={mode === "signup" ? "Min. 8 characters" : "••••••••"} autoComplete={mode === "login" ? "current-password" : "new-password"} />

          {mode === "signup" && (
            <>
              <div style={{ height: 1, background: "var(--border)", margin: "2px 0" }} />
              <div style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>Now, tell us about your puppy 🐶</div>
              <Input label="Puppy's name *" value={puppyName} onChange={setPuppyName} placeholder="Luna" />
              <Input label="Breed (optional)" value={puppyBreed} onChange={setPuppyBreed} placeholder="Australian Shepherd" />
              <Input label="Date of birth (optional)" type="date" value={puppyDob} onChange={setPuppyDob} />
            </>
          )}

          {error && (
            <div style={{ background: "var(--hated)", border: "1px solid var(--hated-border)", borderRadius: "var(--radius-sm)", padding: "9px 12px", fontSize: 12, color: "var(--hated-text)" }}>
              {error}
            </div>
          )}

          <Btn onClick={handle} disabled={loading}>
            {loading ? "Loading…" : mode === "login" ? "Sign In →" : "Create Account →"}
          </Btn>

          {mode === "login" && (
            <div style={{ textAlign: "center", fontSize: 11, color: "var(--muted)" }}>
              <span style={{ textDecoration: "underline", cursor: "pointer" }}>Forgot password?</span>
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: "var(--muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--green)", display: "inline-block" }} />
          Sessions stay active — you won't need to log in every time
        </div>
      </div>
    </div>
  );
};

// ─── SCREEN: HOME ─────────────────────────────────────────────────────────────
const HomeScreen = ({ user, progress, onLogout, onLog }) => {
  const [sheet, setSheet] = useState(null);
  const [reaction, setReaction] = useState(null);
  const [notes, setNotes] = useState("");
  const done = Object.keys(progress).length;
  const pct = Math.round((done / totalItems) * 100);
  const loved = Object.values(progress).filter(p => p.reaction === "loved").length;
  const meh = Object.values(progress).filter(p => p.reaction === "meh").length;
  const hated = Object.values(progress).filter(p => p.reaction === "hated").length;
  const todo = totalItems - done;

  // Build smart suggestions
  const suggestions = [];
  // 1. Hated items first (need counter-conditioning)
  CATEGORIES.forEach(cat => {
    cat.items.forEach(item => {
      const key = `${cat.id}__${item}`;
      if (progress[key]?.reaction === "hated" && suggestions.length < 2) {
        suggestions.push({ cat, item, key, type: "hated", tip: TIPS[cat.id] });
      }
    });
  });
  // 2. Meh items
  CATEGORIES.forEach(cat => {
    cat.items.forEach(item => {
      const key = `${cat.id}__${item}`;
      if (progress[key]?.reaction === "meh" && suggestions.length < 4) {
        suggestions.push({ cat, item, key, type: "meh", tip: TIPS[cat.id] });
      }
    });
  });
  // 3. New items to fill up to 5
  CATEGORIES.forEach(cat => {
    cat.items.forEach(item => {
      const key = `${cat.id}__${item}`;
      if (!progress[key] && suggestions.length < 5) {
        suggestions.push({ cat, item, key, type: "new", tip: null });
      }
    });
  });

  const badgeStyle = (type) => ({
    fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
    padding: "2px 8px", borderRadius: 99, display: "inline-block", marginBottom: 3,
    background: type === "hated" ? "var(--hated)" : type === "meh" ? "var(--meh)" : "#D6EAD6",
    color: type === "hated" ? "var(--hated-text)" : type === "meh" ? "var(--meh-text)" : "var(--loved-text)",
    border: `1px solid ${type === "hated" ? "var(--hated-border)" : type === "meh" ? "var(--meh-border)" : "var(--loved-border)"}`,
  });

  const badgeLabel = { hated: "Needs Work", meh: "Try Again", new: "New" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 18, overflowY: "auto", flex: 1 }} className="slide-up">

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 500 }}>Hello 👋</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 800 }}>{user.puppyName || "Your pup"}'s Journey</div>
        </div>
        <button onClick={onLogout} style={{ fontSize: 11, color: "var(--muted)", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 10px", cursor: "pointer" }}>Sign out</button>
      </div>

      {/* Overall progress */}
      <div style={{ background: "var(--accent)", borderRadius: 18, padding: "16px 18px", color: "white" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 700 }}>Overall Progress</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 800 }}>{done}<span style={{ fontSize: 13, opacity: 0.6 }}>/{totalItems}</span></div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: 99, height: 8, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "white", borderRadius: 99, transition: "width 0.6s cubic-bezier(.22,1,.36,1)" }} />
        </div>
        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>{pct}% complete — every experience counts 🎉</div>
      </div>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
        <StatCard num={loved} label="😍 Loved" color="var(--loved-text)" />
        <StatCard num={meh}   label="😐 Meh"   color="var(--meh-text)" />
        <StatCard num={hated} label="😨 Work"  color="var(--hated-text)" />
        <StatCard num={todo}  label="🔲 To do" color="var(--muted)" />
      </div>

      {/* Try Today */}
      {suggestions.length > 0 && (
        <div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Try Today</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {suggestions.map((s) => (
              <button key={`${s.cat.id}_${s.item}`} onClick={() => {
                const existing = progress[s.key];
                setReaction(existing?.reaction || null);
                setNotes(existing?.notes || "");
                setSheet(s);
              }} style={{
                background: "var(--surface)", borderRadius: "var(--radius)", padding: "12px 14px",
                border: "1px solid var(--border)", boxShadow: "var(--shadow)",
                display: "flex", alignItems: "center", gap: 12,
                cursor: "pointer", textAlign: "left", width: "100%",
              }}
              onMouseDown={e => e.currentTarget.style.transform = "scale(0.98)"}
              onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
              >
                <div style={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }}>{s.cat.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={badgeStyle(s.type)}>{badgeLabel[s.type]}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.item}</div>
                  {s.tip && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>{s.tip}</div>}
                  {!s.tip && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>{s.cat.label} · Tap to log</div>}
                </div>
                <div style={{ fontSize: 20, color: "var(--muted)", flexShrink: 0 }}>›</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Reaction sheet from Home */}
      {sheet && (
        <div className="fade-in" style={{
          position: "fixed", inset: 0, background: "rgba(42,33,24,0.45)",
          display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 100
        }} onClick={e => { if (e.target === e.currentTarget) setSheet(null); }}>
          <div className="sheet-up" style={{
            background: "var(--surface)", borderRadius: "22px 22px 0 0",
            padding: "20px 20px 32px", display: "flex", flexDirection: "column", gap: 14
          }}>
            <div style={{ width: 40, height: 4, borderRadius: 99, background: "var(--border)", margin: "0 auto" }} />
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.08em" }}>How did it go? {sheet.cat.emoji}</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 17, fontWeight: 800, textAlign: "center" }}>{sheet.item}</div>
              {sheet.tip && <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", marginTop: 4, fontStyle: "italic" }}>💡 {sheet.tip}</div>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {["loved", "meh", "hated"].map(r => {
                const rc = reactionColor[r];
                const sel = reaction === r;
                return (
                  <button key={r} onClick={() => setReaction(r)} style={{
                    padding: "12px 6px", borderRadius: "var(--radius)", fontSize: 13, fontWeight: 600, textAlign: "center",
                    border: `2px solid ${sel ? rc.border : "var(--border)"}`,
                    background: sel ? rc.bg : "var(--surface2)", color: sel ? rc.text : "var(--muted)",
                    cursor: "pointer", transition: "all 0.15s", transform: sel ? "scale(1.04)" : "scale(1)"
                  }}>
                    <div style={{ fontSize: 22 }}>{reactionEmoji[r]}</div>
                    <div style={{ fontSize: 10, marginTop: 3 }}>{reactionLabel[r]}</div>
                  </button>
                );
              })}
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Notes (optional)</div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="e.g. tried from a distance, gave treats..."
                style={{
                  width: "100%", height: 60, background: "var(--surface2)", border: "1.5px solid var(--border)",
                  borderRadius: "var(--radius-sm)", padding: "9px 12px", fontSize: 13, resize: "none",
                  color: "var(--text)", outline: "none", fontFamily: "'DM Sans', sans-serif"
                }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10 }}>
              <Btn variant="secondary" onClick={() => setSheet(null)}>Cancel</Btn>
              <Btn onClick={() => {
                if (!reaction) return;
                onLog(sheet.key, { reaction, notes, date: new Date().toISOString() });
                setSheet(null);
              }} disabled={!reaction}>Save →</Btn>
            </div>
          </div>
        </div>
      )}

      {done === totalItems && (
        <div style={{ background: "var(--loved)", border: "1px solid var(--loved-border)", borderRadius: "var(--radius)", padding: 18, textAlign: "center" }}>
          <div style={{ fontSize: 32 }}>🎉</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: 16, color: "var(--loved-text)", marginTop: 6 }}>Amazing! All done!</div>
          <div style={{ fontSize: 12, color: "var(--loved-text)", opacity: 0.8, marginTop: 4 }}>{user.puppyName || "Your pup"} has experienced everything on the list!</div>
        </div>
      )}
    </div>
  );
};

// ─── SCREEN: CHECKLIST ────────────────────────────────────────────────────────
const ChecklistScreen = ({ progress, onLog }) => {
  const [activeCat, setActiveCat] = useState(CATEGORIES[0].id);
  const [sheet, setSheet] = useState(null); // { cat, item, key }
  const [reaction, setReaction] = useState(null);
  const [notes, setNotes] = useState("");

  const cat = CATEGORIES.find(c => c.id === activeCat);
  const catDone = cat.items.filter(i => progress[`${cat.id}__${i}`]).length;
  const pct = Math.round((catDone / cat.items.length) * 100);

  const openSheet = (item) => {
    const key = `${cat.id}__${item}`;
    const existing = progress[key];
    setReaction(existing?.reaction || null);
    setNotes(existing?.notes || "");
    setSheet({ cat, item, key });
  };

  const saveReaction = () => {
    if (!reaction) return;
    onLog(sheet.key, { reaction, notes, date: new Date().toISOString() });
    setSheet(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }} className="slide-up">

      {/* Header */}
      <div style={{ padding: "16px 18px 0" }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 800, marginBottom: 12 }}>Checklist</div>

        {/* Category pills */}
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 12 }}>
          {CATEGORIES.map(c => {
            const done = c.items.filter(i => progress[`${c.id}__${i}`]).length;
            const isActive = c.id === activeCat;
            return (
              <button key={c.id} onClick={() => setActiveCat(c.id)} style={{
                flexShrink: 0, padding: "6px 13px", borderRadius: 99, fontSize: 12, fontWeight: 500,
                background: isActive ? "var(--accent)" : "var(--surface)", color: isActive ? "white" : "var(--muted)",
                border: `1.5px solid ${isActive ? "var(--accent)" : "var(--border)"}`,
                cursor: "pointer", transition: "all 0.18s", whiteSpace: "nowrap"
              }}>
                {c.emoji} {c.label} <span style={{ opacity: 0.7, fontSize: 10 }}>({done}/{c.items.length})</span>
              </button>
            );
          })}
        </div>

        {/* Progress */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>{cat.label} — {catDone} of {cat.items.length}</span>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>{pct}%</span>
          </div>
          <ProgressBar value={pct} color={pct === 100 ? "var(--green)" : "var(--accent)"} />
        </div>
        <div style={{ height: 1, background: "var(--border)" }} />
      </div>

      {/* Items */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 18px" }}>
        {cat.items.map((item, i) => {
          const key = `${cat.id}__${item}`;
          const p = progress[key];
          const rc = p ? reactionColor[p.reaction] : null;
          return (
            <div key={item}>
              <button onClick={() => openSheet(item)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "11px 0",
                background: "none", border: "none", cursor: "pointer", textAlign: "left"
              }}>
                {/* Checkbox */}
                <div style={{
                  width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                  border: p ? "none" : "2px solid var(--border)",
                  background: p ? "var(--accent)" : "white",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  animation: p ? "checkPop 0.3s ease" : "none",
                  transition: "background 0.2s"
                }}>
                  {p && <span style={{ color: "white", fontSize: 12, fontWeight: 700 }}>✓</span>}
                </div>
                {/* Text */}
                <span style={{ flex: 1, fontSize: 13, color: p ? "var(--muted)" : "var(--text)", textDecoration: p ? "line-through" : "none" }}>{item}</span>
                {/* Reaction badge */}
                {p && (
                  <span style={{
                    fontSize: 11, padding: "3px 9px", borderRadius: 99,
                    background: rc.bg, border: `1px solid ${rc.border}`, color: rc.text, fontWeight: 500
                  }}>
                    {reactionEmoji[p.reaction]} {reactionLabel[p.reaction]}
                  </span>
                )}
              </button>
              {i < cat.items.length - 1 && <div style={{ height: 1, background: "var(--border)" }} />}
            </div>
          );
        })}
        <div style={{ height: 20 }} />
      </div>

      {/* Bottom sheet */}
      {sheet && (
        <div className="fade-in" style={{
          position: "fixed", inset: 0, background: "rgba(42,33,24,0.45)",
          display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 100
        }} onClick={e => { if (e.target === e.currentTarget) setSheet(null); }}>
          <div className="sheet-up" style={{
            background: "var(--surface)", borderRadius: "22px 22px 0 0",
            padding: "20px 20px 32px", display: "flex", flexDirection: "column", gap: 14
          }}>
            <div style={{ width: 40, height: 4, borderRadius: 99, background: "var(--border)", margin: "0 auto" }} />
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.08em" }}>How did {sheet.cat.emoji} go?</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 17, fontWeight: 800, textAlign: "center" }}>{sheet.item}</div>
              {progress[sheet.key] && <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", marginTop: 3 }}>Logged before — update reaction below</div>}
            </div>

            {/* Reaction buttons */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {["loved", "meh", "hated"].map(r => {
                const rc = reactionColor[r];
                const sel = reaction === r;
                return (
                  <button key={r} onClick={() => setReaction(r)} style={{
                    padding: "12px 6px", borderRadius: "var(--radius)", fontSize: 13, fontWeight: 600, textAlign: "center",
                    border: `2px solid ${sel ? rc.border : "var(--border)"}`,
                    background: sel ? rc.bg : "var(--surface2)", color: sel ? rc.text : "var(--muted)",
                    cursor: "pointer", transition: "all 0.15s", transform: sel ? "scale(1.04)" : "scale(1)"
                  }}>
                    <div style={{ fontSize: 22 }}>{reactionEmoji[r]}</div>
                    <div style={{ fontSize: 10, marginTop: 3, letterSpacing: "0.04em" }}>{reactionLabel[r]}</div>
                  </button>
                );
              })}
            </div>

            {/* Notes */}
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Notes (optional)</div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="e.g. tried from a distance, gave treats..."
                style={{
                  width: "100%", height: 64, background: "var(--surface2)", border: "1.5px solid var(--border)",
                  borderRadius: "var(--radius-sm)", padding: "9px 12px", fontSize: 13, resize: "none",
                  color: "var(--text)", outline: "none", fontFamily: "'DM Sans', sans-serif"
                }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10 }}>
              <Btn variant="secondary" onClick={() => setSheet(null)}>Cancel</Btn>
              <Btn onClick={saveReaction} disabled={!reaction}>Save →</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── SCREEN: PROGRESS ────────────────────────────────────────────────────────
const ProgressScreen = ({ user, progress }) => {
  const done = Object.keys(progress).length;
  const pct = Math.round((done / totalItems) * 100);
  const needsWork = [];

  CATEGORIES.forEach(cat => {
    cat.items.forEach(item => {
      const key = `${cat.id}__${item}`;
      if (progress[key]?.reaction === "hated") {
        needsWork.push({ cat, item, notes: progress[key].notes });
      }
    });
  });

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 18 }} className="slide-up">
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 800, marginBottom: 14 }}>
        {user.puppyName || "Your pup"}'s Report
      </div>

      {/* Hero */}
      <div style={{ background: "var(--accent)", borderRadius: 20, padding: "20px 18px", color: "white", textAlign: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 56, fontWeight: 800, lineHeight: 1 }}>{done}</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 3 }}>of {totalItems} experiences completed</div>
        <div style={{ margin: "12px 0 6px", background: "rgba(255,255,255,0.2)", borderRadius: 99, height: 8, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "white", borderRadius: 99, transition: "width 0.6s cubic-bezier(.22,1,.36,1)" }} />
        </div>
        <div style={{ fontSize: 12, opacity: 0.65 }}>{pct}% complete</div>
      </div>

      {/* Per-category */}
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 13, fontWeight: 700, marginBottom: 10 }}>By Category</div>
      <div style={{ background: "var(--surface)", borderRadius: "var(--radius)", border: "1px solid var(--border)", overflow: "hidden", marginBottom: 16 }}>
        {CATEGORIES.map((cat, i) => {
          const catDone = cat.items.filter(item => progress[`${cat.id}__${item}`]).length;
          const catPct = Math.round((catDone / cat.items.length) * 100);
          const catHated = cat.items.filter(item => progress[`${cat.id}__${item}`]?.reaction === "hated").length;
          return (
            <div key={cat.id} style={{ padding: "11px 14px", borderBottom: i < CATEGORIES.length - 1 ? "1px solid var(--border)" : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 13 }}>{cat.emoji} {cat.label}</span>
                <span style={{ fontSize: 12, color: "var(--muted)" }}>{catDone}/{cat.items.length}</span>
              </div>
              <ProgressBar value={catPct} color={catPct === 100 ? "var(--green)" : catHated > 0 ? "var(--accent2)" : "var(--accent)"} height={6} />
            </div>
          );
        })}
      </div>

      {/* Needs work */}
      {needsWork.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 13, fontWeight: 700, marginBottom: 10 }}>⚠️ Needs Work</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {needsWork.map(({ cat, item, notes }) => (
              <div key={`${cat.id}_${item}`} style={{ background: "var(--hated)", border: "1px solid var(--hated-border)", borderRadius: "var(--radius)", padding: "11px 14px" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 18 }}>{cat.emoji}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--hated-text)" }}>{item}</div>
                    <div style={{ fontSize: 11, color: "var(--hated-text)", opacity: 0.8 }}>{TIPS[cat.id]}</div>
                    {notes && <div style={{ fontSize: 11, color: "var(--hated-text)", opacity: 0.7, marginTop: 2, fontStyle: "italic" }}>"{notes}"</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {needsWork.length === 0 && done > 0 && (
        <div style={{ background: "var(--loved)", border: "1px solid var(--loved-border)", borderRadius: "var(--radius)", padding: 14, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: "var(--loved-text)", fontWeight: 600 }}>🌟 No items need work — great job!</div>
        </div>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
};

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState({});
  const [tab, setTab] = useState("home");
  const [authChecked, setAuthChecked] = useState(false);

  // Restore persistent session on mount
  useEffect(() => {
    const session = loadSession();
    if (session) {
      setUser(session);
      setProgress(loadProgress(session.uid));
    }
    setAuthChecked(true);
  }, []);

  const handleAuth = useCallback((u) => {
    setUser(u);
    setProgress(loadProgress(u.uid));
    setTab("home");
  }, []);

  const handleLogout = useCallback(async () => {
    await simulateLogout();
    clearSession();
    setUser(null);
    setProgress({});
  }, []);

  const handleLog = useCallback((key, data) => {
    setProgress(prev => {
      const next = { ...prev, [key]: data };
      if (user) saveProgress(user.uid, next);
      return next;
    });
  }, [user]);

  if (!authChecked) return null;
  if (!user) return <AuthScreen onAuth={handleAuth} />;

  return (
    <div style={{
      maxWidth: 480, margin: "0 auto", minHeight: "100vh",
      display: "flex", flexDirection: "column",
      background: "var(--bg)", position: "relative"
    }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", paddingTop: "env(safe-area-inset-top, 0px)" }}>
        {tab === "home"      && <HomeScreen      user={user} progress={progress} onLogout={handleLogout} onLog={handleLog} />}
        {tab === "checklist" && <ChecklistScreen progress={progress} onLog={handleLog} />}
        {tab === "progress"  && <ProgressScreen  user={user} progress={progress} />}
      </div>
      <BottomNav tab={tab} setTab={setTab} />
    </div>
  );
}
